import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAgentDetail } from '../../../helpers/agents';
import { TERMINAL_STATUSES, BUSY_STATUSES } from '../constants';
import useAgentStream from '../../../hooks/useAgentStream';

export default function useAgentDetail({ selectedAgentId }) {
  const messagesEndRef = useRef(null);

  const [agentDetail, setAgentDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [pollingNonce, setPollingNonce] = useState(0);

  const fetchDetail = useCallback(
    async (withSpinner = false) => {
      if (!selectedAgentId) return null;
      if (withSpinner) setDetailLoading(true);
      setDetailError('');
      try {
        const response = await getAgentDetail(selectedAgentId);
        setAgentDetail(response.data);
        return response.data?.status;
      } catch {
        setDetailError('Unable to load agent detail');
        return null;
      } finally {
        if (withSpinner) setDetailLoading(false);
      }
    },
    [selectedAgentId]
  );

  const { wsMessages, wsStatus } = useAgentStream(selectedAgentId);
  const wsStatusRef = useRef(wsStatus);
  useEffect(() => { wsStatusRef.current = wsStatus; }, [wsStatus]);

  // Polling effect
  useEffect(() => {
    if (!selectedAgentId) return undefined;
    let cancelled = false;
    let timeoutId;

    const poll = async (withSpinner = false) => {
      const status = await fetchDetail(withSpinner);
      if (cancelled) return;
      const isBusy = status && !TERMINAL_STATUSES.includes(status);
      const wsOpen = wsStatusRef.current === 'open';
      if (isBusy && !wsOpen) {
        timeoutId = setTimeout(() => poll(false), 4000);
      }
    };

    poll(true);

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [selectedAgentId, fetchDetail, pollingNonce]);

  const messageCount = agentDetail?.messages?.length ?? 0;

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (!messagesEndRef.current) return;
    messagesEndRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
      inline: 'nearest',
    });
  }, [messageCount, selectedAgentId]);

  const busy = useMemo(() => BUSY_STATUSES.includes(agentDetail?.status), [agentDetail?.status]);

  // Merge WS messages into agentDetail
  useEffect(() => {
    if (!wsMessages.length) return;
    const latest = wsMessages[wsMessages.length - 1];

    setAgentDetail((prev) => {
      if (!prev) return prev;
      let updated = { ...prev };

      if (latest.status && typeof latest.status === 'string') {
        updated.status = latest.status;
      }

      const RENDERABLE_ROLES = ['user', 'assistant', 'tool_call', 'tool_output'];
      if (latest.role && RENDERABLE_ROLES.includes(latest.role)) {
        const newMsg = {
          role: latest.role,
          content: latest.content,
          timestamp: latest.timestamp,
          tool_name: latest.tool_name,
          tool_args: latest.tool_args,
          tool_output: latest.tool_output,
        };
        const messages = prev.messages || [];
        const streamingIdx = messages.findLastIndex((m) => m._streaming);
        if (streamingIdx !== -1 && latest.role === 'assistant') {
          updated.messages = [
            ...messages.slice(0, streamingIdx),
            newMsg,
            ...messages.slice(streamingIdx + 1),
          ];
        } else {
          const exists = messages.some(
            (m) => m.timestamp === newMsg.timestamp && m.role === newMsg.role
          );
          if (!exists) {
            updated.messages = [...messages, newMsg];
          }
        }
      } else if (latest.content && !latest.role) {
        const messages = prev.messages || [];
        const streamingIdx = messages.findLastIndex((m) => m._streaming);
        if (streamingIdx !== -1) {
          const existing = messages[streamingIdx];
          updated.messages = [
            ...messages.slice(0, streamingIdx),
            { ...existing, content: (existing.content || '') + latest.content },
            ...messages.slice(streamingIdx + 1),
          ];
        } else {
          updated.messages = [
            ...messages,
            { role: 'assistant', content: latest.content, timestamp: latest.timestamp, _streaming: true },
          ];
        }
      }

      return updated;
    });

    if (latest.done) {
      fetchDetail(false);
    }
  }, [wsMessages, fetchDetail]);

  // Resume polling when WS closes while agent is busy
  useEffect(() => {
    if (wsStatus === 'closed' && busy) {
      setPollingNonce((prev) => prev + 1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsStatus]);

  return {
    agentDetail,
    setAgentDetail,
    detailLoading,
    detailError,
    fetchDetail,
    pollingNonce,
    setPollingNonce,
    busy,
    messagesEndRef,
  };
}
